import {useEffect, useState} from 'react';

import {
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Stack,
} from '@zoom/react-ui';
import {IconEyeHide, IconEyeShow, IconQuestionCircleFill} from '@zoom/sun-ui-icon';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {ZmPrimaryBtn, ZmTertiaryBtn} from 'Components/ZmButtons';
import {ZmDialog} from 'Components/ZmDialogs';
import {ZmTextField} from 'Components/ZmInputs';
import {connectAccount} from 'Store/IntegrationStore';
import {ICLOUD_APP_SPECIFIC_PASSWORD_LINK, PROVIDER_TYPE} from 'Utils/consts';
import {openUrlInBrowser} from 'Utils/ZoomClientUtils';


const ConnectICloudDialog = ({iCloudEmail, isSubmitting, onClose, onSuccess, open}) => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const isPasswordFormatInvalid = password && !/^\w{4}-\w{4}-\w{4}-\w{4}$/.test(password);
  useEffect(() => {
    // when re-authorize, the email is fixed, and user needs to re-enter the new password
    setEmail(iCloudEmail);
  }, [iCloudEmail]);
  const onConnect = async () => {
    const iCloudAccount = {
      mail: email,
      password,
      service: PROVIDER_TYPE.APPLE,
    };
    const {meta} = await dispatch(
      connectAccount(iCloudAccount)
    );
    if (meta.requestStatus === 'fulfilled') {
      onSuccess?.(iCloudAccount);
    }
  };
  return (
    <ZmDialog open={open}>
      <DialogTitle>
        {t('integration.connectToApple')}
      </DialogTitle>
      <DialogContent>
        <Stack direction="column">
          <ZmTextField
            autoFocus
            placeholder={t('integration.iCloudEmail')}
            value={email}
            required
            disabled={!!iCloudEmail}
            onChange={(e) => {
              setEmail(e.target.value.trim());
            }}
            sx={{width: '280px', marginTop: '10px'}}
          />
          <Stack direction="row" alignItems="center" gap="6px">
            <ZmTextField
              value={password}
              placeholder={t('integration.iCloudPassword')}
              required
              type={showPassword ? 'text' : 'password'}
              error={isPasswordFormatInvalid}
              helperText={isPasswordFormatInvalid? t('integration.iCloudPasswordTip', {defaultValue: ''}): ''}
              onChange={(e) => {
                setPassword(e.target.value.trim());
              }}
              sx={{marginY: '10px', width: '280px'}}
              InputProps={{
                endAdornment: <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((show) => !show)}
                    edge="end"
                  >
                    {showPassword ? <IconEyeHide size={15} /> : <IconEyeShow size={15} />}
                  </IconButton>
                </InputAdornment>,
              }}
            />
            <IconButton sx={{width: '26px', height: '26px'}}
              onClick={() => openUrlInBrowser(ICLOUD_APP_SPECIFIC_PASSWORD_LINK)}>
              <IconQuestionCircleFill size={18} />
            </IconButton>
          </Stack>
          <Stack sx={{width: '100%'}} gap="10px" direction="row" justifyContent="flex-end">
            <ZmTertiaryBtn
              onClick={onClose}
              disabled={isSubmitting}
              size="small"
            >
              {t('common.cancel')}
            </ZmTertiaryBtn>
            <ZmPrimaryBtn
              onClick={onConnect}
              disabled={!email || !password || isPasswordFormatInvalid || isSubmitting}
              size="small"
            >
              {isSubmitting && <CircularProgress size={20} />}
              {t('integration.connect')}
            </ZmPrimaryBtn>
          </Stack>
        </Stack>
      </DialogContent>
    </ZmDialog>
  );
};
export default ConnectICloudDialog;
